import { ShipBuilderState } from "../types/ship";
import { OptionalSystemsState } from "../types/optionalSystems";
import { BreakawayHullState } from "../types/breakawayHull";
import { ModuleState } from "../store/features/moduleSlice";
import { CrewState } from "../domains/crew/types";
import { HullState } from "../domains/hull/types";
import { PowerState } from "../domains/power/types";
import { DrivesState } from "../domains/drives/types";
import { WeaponsState } from "../domains/weapons/types";
import { RootState } from "../store";

export interface SaveData {
  version: string;
  timestamp: number;
  shipData: ShipBuilderState;
  optionalSystems: OptionalSystemsState;
  breakawayHull: BreakawayHullState;
  module: ModuleState;
  crew: CrewState;
  hull: HullState;
  power: PowerState;
  drives: DrivesState;
  weapons: WeaponsState;
  metadata: {
    shipName: string;
    shipClass: string;
    description?: string;
  };
}

const CURRENT_VERSION = "1.0.0";

export const saveShipDesign = (state: RootState, filename?: string): void => {
  try {
    // Determine if this is a module or ship design based on current context
    const isModuleDesign = state.module.moduleConfig && !state.shipBuilder.shipDetails.name;
    
    const saveData: SaveData = {
      version: CURRENT_VERSION,
      timestamp: Date.now(),
      shipData: state.shipBuilder,
      optionalSystems: state.optionalSystems,
      breakawayHull: state.breakawayHull,
      module: state.module,
      crew: state.crew,
      hull: state.hull,
      power: state.power,
      drives: state.drives,
      weapons: state.weapons,
      metadata: isModuleDesign ? {
        shipName: state.module.moduleConfig?.name || "Unnamed Module",
        shipClass: "Module",
        description: state.module.moduleConfig?.description || undefined,
      } : {
        shipName: state.shipBuilder.finalDetails?.name || state.shipBuilder.shipDetails?.name || "Unnamed Ship",
        shipClass: state.shipBuilder.finalDetails?.class || state.shipBuilder.shipDetails?.class || "Unknown Class",
        description: state.shipBuilder.finalDetails?.description || undefined,
      },
    };

    const dataStr = JSON.stringify(saveData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });

    // Generate filename if not provided
    const name = isModuleDesign 
      ? (state.module.moduleConfig?.name || "module").replace(/[^a-zA-Z0-9]/g, '_')
      : (state.shipBuilder.finalDetails?.name || state.shipBuilder.shipDetails?.name || "ship").replace(/[^a-zA-Z0-9]/g, '_');
    const extension = isModuleDesign ? "TM2" : "MT2";
    const defaultFilename = `${name}_${new Date().toISOString().split('T')[0]}.${extension}`;
    const finalFilename = filename || defaultFilename;

    // Create download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(dataBlob);
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Error saving ship design:", error);
    alert("Error saving ship design. Please try again.");
  }
};

export const loadShipDesign = (): Promise<Partial<RootState> | null> => {
  return new Promise((resolve) => {
    try {
      console.log("loadShipDesign: Creating file input...");
      // Create file input
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".MT2,.mt2,application/json";
      console.log("loadShipDesign: File input created with accept:", input.accept);
      
      // Only set display style if it exists (for test environment)
      if (input.style) {
        input.style.display = "none";
      }

      let resolved = false;

      // Handle file selection
      input.onchange = (event) => {
        console.log("loadShipDesign: File input onchange triggered");
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        
        const file = (event.target as HTMLInputElement).files?.[0];
        console.log("loadShipDesign: Selected file:", file);
        if (!file) {
          console.log("loadShipDesign: No file selected, resolving with null");
          resolve(null);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const saveData: SaveData = JSON.parse(content);

            // Check if this is an old module file format (moduleData structure)
            if ((saveData as any).moduleData) {
              // Convert old module format to new format
              const oldModuleData = (saveData as any).moduleData;
              const convertedState: Partial<RootState> = {
                module: {
                  currentStep: 0,
                  moduleData: oldModuleData,
                  moduleConfig: {
                    name: oldModuleData.name || "",
                    size: oldModuleData.tonnage || 100,
                    techLevel: oldModuleData.techLevel || 12,
                    description: oldModuleData.description || "",
                    systems: {
                      optionalSystems: oldModuleData.moduleConfig?.optionalSystems || [],
                      powerPlant: oldModuleData.moduleConfig?.powerPlant ? [oldModuleData.moduleConfig.powerPlant] : [],
                      computer: oldModuleData.moduleConfig?.computer || [],
                      computerOptions: oldModuleData.moduleConfig?.computerOptions || {},
                      software: oldModuleData.moduleConfig?.software || {
                        installed: [
                          { packageId: "manoeuvre", level: 0, installed: true },
                          { packageId: "intellect", level: 0, installed: true },
                          { packageId: "library", level: 0, installed: true },
                        ],
                        running: [
                          { packageId: "manoeuvre", level: 0, running: true },
                          { packageId: "intellect", level: 0, running: true },
                          { packageId: "library", level: 0, running: true },
                        ],
                        expertSkills: {},
                        translatorLanguages: {},
                        databaseTopics: {},
                        virtualCrewRoles: {},
                        virtualGunnerRoles: {},
                      },
                      sensors: oldModuleData.moduleConfig?.sensors,
                      weapons: oldModuleData.moduleConfig?.weapons || [],
                      weaponMounts: oldModuleData.moduleConfig?.weaponMounts || [],
                      defensiveSystems: oldModuleData.moduleConfig?.defensiveSystems || [],
                      ammunitionStorage: oldModuleData.moduleConfig?.ammunitionStorage || [],
                      drives: oldModuleData.moduleConfig?.drives || [],
                      crew: oldModuleData.moduleConfig?.crew,
                      staterooms: oldModuleData.moduleConfig?.staterooms || [],
                      stateroomConfig: oldModuleData.moduleConfig?.stateroomConfig,
                      cargo: oldModuleData.moduleConfig?.cargo,
                      fuel: oldModuleData.moduleConfig?.fuel,
                    },
                    calculations: {
                      totalCost: oldModuleData.fitoutCost || 0,
                      totalWeight: oldModuleData.fitoutWeight || 0,
                      totalPower: oldModuleData.fitoutPower || 0,
                      remainingTons: (oldModuleData.tonnage || 100) - (oldModuleData.fitoutWeight || 0),
                      crewRequirements: 0,
                      totalCrew: 0,
                      totalPassengers: 0,
                      monthlyCost: 0,
                    },
                    supplyUnits: oldModuleData.moduleConfig?.supplyUnits,
                    supplyTons: oldModuleData.moduleConfig?.supplyTons,
                  },
                  validation: {
                    isValid: true,
                    errors: [],
                    warnings: [],
                  },
                  isDirty: false,
                  lastSaved: null,
                  version: "1.0.0",
                },
              };
              resolve(convertedState);
              return;
            }

            // Validate file format - for ship builder, only accept files with ship data
            if (!saveData.version || !saveData.shipData) {
              if (typeof window !== 'undefined' && window.alert) {
                window.alert("Invalid file format. This doesn't appear to be a valid ship design file (.MT2). Please use a ship save file, not a module file.");
              }
              resolve(null);
              return;
            }

            // Check version compatibility (basic check for now)
            if (saveData.version !== CURRENT_VERSION) {
              const shouldContinue = window.confirm(
                `This file was saved with version ${saveData.version}, but the current version is ${CURRENT_VERSION}. ` +
                "Loading it may cause compatibility issues. Do you want to continue?"
              );
              if (!shouldContinue) {
                resolve(null);
                return;
              }
            }

            // Return the full state for loading
            const loadedState: Partial<RootState> = {
              shipBuilder: saveData.shipData,
              optionalSystems: saveData.optionalSystems,
              breakawayHull: saveData.breakawayHull,
              module: saveData.module,
              crew: saveData.crew,
              hull: saveData.hull,
              power: saveData.power,
              drives: saveData.drives,
              weapons: saveData.weapons,
            };
            resolve(loadedState);
          } catch (error) {
            console.error("Error parsing ship design file:", error);
            if (typeof window !== 'undefined' && window.alert) {
              window.alert("Error reading ship design file. The file may be corrupted or in an invalid format.");
            }
            resolve(null);
          }
        };

        reader.onerror = () => {
          if (typeof window !== 'undefined' && window.alert) {
            window.alert("Error reading file. Please try again.");
          }
          resolve(null);
        };

        reader.readAsText(file);
      };

      // Use a timeout to detect if no file is selected within a reasonable time
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          console.log("loadShipDesign: Timeout reached, no file selected");
          resolved = true;
          resolve(null);
        }
      }, 30000); // 30 second timeout

      // Trigger file selection
      if (document.body && typeof document.body.appendChild === 'function') {
        console.log("loadShipDesign: Adding input to DOM and triggering click");
        document.body.appendChild(input);
        input.click();
        console.log("loadShipDesign: File dialog should now be open");
        
        // No need for focus listener anymore - using timeout instead
        
        document.body.removeChild(input);
      } else {
        console.log("loadShipDesign: Document body not available, resolving with null");
        resolve(null);
      }
    } catch (error) {
      console.error("Error setting up file load:", error);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert("Error setting up file load. Please try again.");
      }
      resolve(null);
    }
  });
};

export const getDefaultFilename = (state: RootState): string => {
  // Determine if this is a module or ship design
  const isModuleDesign = state.module.moduleConfig && !state.shipBuilder.shipDetails.name;
  
  const name = isModuleDesign 
    ? (state.module.moduleConfig?.name || "Module_Design").replace(/[^a-zA-Z0-9]/g, '_')
    : (state.shipBuilder.shipDetails.name || "Ship_Design").replace(/[^a-zA-Z0-9]/g, '_');
  
  const now = new Date();
  const dateStr = now.getFullYear().toString() + 
    (now.getMonth() + 1).toString().padStart(2, '0') + 
    now.getDate().toString().padStart(2, '0');
  const timeStr = now.getHours().toString().padStart(2, '0') + 
    now.getMinutes().toString().padStart(2, '0') + 
    now.getSeconds().toString().padStart(2, '0');
  
  const extension = isModuleDesign ? "TM2" : "MT2";
  return `${name}_${dateStr}_${timeStr}.${extension}`;
}; 